import CircleIcon from "@mui/icons-material/Circle";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import { FamilyHistory, MedicalHistory, SurgicalHistory } from "@/services";
import {
  EntrySection,
  FAMILY_HISTORY_SECTION,
  MEDICAL_HISTORY_SECTION,
  SURGICAL_HISTORY_SECTION,
  buildEntryLine,
} from "@/utils/subjective-entry";
import { FILED_LABEL_FONTSIZE } from "@/utils/utils";
import LoadingAndEmptyStateWrapper from "./commonWrappers/LoadingAndEmptyStateWrapper";

interface HistoryListResponse<T> {
  data?: { content?: T[] };
}

interface HistoryProps {
  medicalHistoryList?: HistoryListResponse<MedicalHistory>;
  surgicalHistoryList?: HistoryListResponse<SurgicalHistory>;
  familyHistoryList?: HistoryListResponse<FamilyHistory>;
  medicalHistoryLoading: boolean;
  surgicalHistoryLoading: boolean;
  familyHistoryLoading: boolean;
}

interface HistoryGroupProps<T> {
  label: string;
  section: EntrySection<T>;
  rows: T[];
}

const HistoryGroup = <T,>({ label, section, rows }: HistoryGroupProps<T>) => {
  if (!rows.length) return null;
  return (
    <>
      <Flex alignItems="center" gap={1}>
        <CircleIcon sx={{ fontSize: 10 }} />
        <Text
          fontWeight={600}
          color="text.secondary"
          fontSize={FILED_LABEL_FONTSIZE}
        >
          {label}
        </Text>
      </Flex>
      {rows.map((row, index) => (
        <Text key={index} isTabletext>
          {index + 1}. {buildEntryLine(section, row)}
        </Text>
      ))}
    </>
  );
};

const History = ({
  medicalHistoryList,
  surgicalHistoryList,
  familyHistoryList,
}: HistoryProps) => {
  const medicalRows = medicalHistoryList?.data?.content ?? [];
  const surgicalRows = surgicalHistoryList?.data?.content ?? [];
  const familyRows = familyHistoryList?.data?.content ?? [];
  const isEmpty =
    !medicalRows.length && !surgicalRows.length && !familyRows.length;

  if (isEmpty) {
    return (
      <LoadingAndEmptyStateWrapper
        loading={false}
        isEmpty
        emptyMessage="No History found"
      />
    );
  }

  return (
    <Flex flexDirection="column" width="100%" gap={1} mt={2} ml={1}>
      <HistoryGroup
        label="Past Medical History"
        section={MEDICAL_HISTORY_SECTION}
        rows={medicalRows}
      />
      <HistoryGroup
        label="Past Surgical History"
        section={SURGICAL_HISTORY_SECTION}
        rows={surgicalRows}
      />
      <HistoryGroup
        label="Family History"
        section={FAMILY_HISTORY_SECTION}
        rows={familyRows}
      />
    </Flex>
  );
};

export default History;
